import { Stock } from "@models/Stock";
import { supabase } from "@supabaseClient/supabaseClient";
import { fetchAllPages } from "@utils/fetchAllPages";

export type StockMetadata = {
  [key: string]: string | number | boolean | null;
};

export class StockClient {
  static async getStockByOrganizationId(
    organization_id: string
  ): Promise<Stock[]> {
    // Paginated: an org's inventory routinely exceeds the PostgREST 1000-row
    // cap (especially after a CSV import), and an unbounded select would
    // silently truncate — leaving products beyond the cap showing zero stock.
    return fetchAllPages<Stock>((from, to) =>
      supabase
        .from("inventory")
        .select("*")
        .eq("organization_id", organization_id)
        .order("id", { ascending: true })
        .range(from, to)
    );
  }

  static async createStock(
    stock: Omit<Stock, "id" | "created_at" | "updated_at">
  ): Promise<Stock> {
    const { data, error } = await supabase
      .from("inventory")
      .insert([stock])
      .select();
    if (error) {
      throw error;
    }
    return data[0];
  }

  static async updateStock(
    stock: Omit<Stock, "created_at" | "updated_at">
  ): Promise<Stock> {
    const { data, error } = await supabase
      .from("inventory")
      .update(stock)
      .eq("id", stock.id)
      .select();
    if (error) {
      throw error;
    }
    return data[0];
  }

  static async deleteStock(id: string): Promise<Stock> {
    const { data, error } = await supabase
      .from("inventory")
      .delete()
      .eq("id", id)
      .select();
    if (error) {
      throw error;
    }
    return data[0];
  }

  /**
   * Relocate a stock entry to another location. A product can only have one
   * entry per location, so if the destination already stocks this product the
   * two are folded together (quantities summed) and the source row removed —
   * never two rows for the same product+location.
   *
   * Returns `merged: true` when it combined into an existing entry.
   *
   * NOTE: not yet transactional — the merge is an update-then-delete. Order is
   * deliberate (destination is updated before the source is dropped) so a
   * mid-failure over-counts rather than losing stock. Phase 2 moves this into
   * a Postgres function for atomicity once the DB unique index lands.
   */
  static async moveStock(
    source: Stock,
    targetLocationId: string
  ): Promise<{ merged: boolean }> {
    const { data: existingRows, error: findError } = await supabase
      .from("inventory")
      .select("*")
      .eq("product_id", source.product_id)
      .eq("location_id", targetLocationId)
      .limit(1);
    if (findError) {
      throw findError;
    }

    const existing = existingRows?.[0] as Stock | undefined;

    if (existing) {
      const { error: updateError } = await supabase
        .from("inventory")
        .update({
          quantity: existing.quantity + source.quantity,
          committed_quantity:
            existing.committed_quantity + source.committed_quantity,
        })
        .eq("id", existing.id);
      if (updateError) {
        throw updateError;
      }

      const { error: deleteError } = await supabase
        .from("inventory")
        .delete()
        .eq("id", source.id);
      if (deleteError) {
        throw deleteError;
      }

      return { merged: true };
    }

    const { error: moveError } = await supabase
      .from("inventory")
      .update({ location_id: targetLocationId })
      .eq("id", source.id);
    if (moveError) {
      throw moveError;
    }

    return { merged: false };
  }

  static async getStockByProductAndLocation(
    product_id: string,
    location_id: string
  ): Promise<Stock | null> {
    const { data, error } = await supabase
      .from("inventory")
      .select("*")
      .eq("product_id", product_id)
      .eq("location_id", location_id)
      .limit(1)
      .maybeSingle();
    if (error) {
      throw error;
    }
    return data;
  }

  static async getStockById(id: string): Promise<Stock | null> {
    const { data, error } = await supabase
      .from("inventory")
      .select("*")
      .eq("id", id)
      .maybeSingle();
    if (error) {
      throw error;
    }
    return data;
  }

  static async getInventoryByProductId(product_id: string): Promise<Stock[]> {
    const { data, error } = await supabase
      .from("inventory")
      .select("*")
      .eq("product_id", product_id);
    if (error) {
      throw error;
    }
    return data;
  }

  // Stock rows for a set of products in one round-trip — used by the hub's
  // global search, which matches products client-side then pulls only the
  // stock for the handful that matched.
  static async getInventoryByProductIds(
    product_ids: string[]
  ): Promise<Stock[]> {
    if (product_ids.length === 0) return [];
    const { data, error } = await supabase
      .from("inventory")
      .select("*")
      .in("product_id", product_ids);
    if (error) {
      throw error;
    }
    return data;
  }

  static async getInventoryByLocationId(location_id: string): Promise<Stock[]> {
    const { data, error } = await supabase
      .from("inventory")
      .select("*")
      .eq("location_id", location_id);
    if (error) {
      throw error;
    }
    return data;
  }

  static async getInventoryByProductIdAndLocationId(
    product_id: string,
    location_id: string
  ): Promise<Stock[]> {
    const { data, error } = await supabase
      .from("inventory")
      .select("*")
      .eq("product_id", product_id)
      .eq("location_id", location_id);
    if (error) {
      throw error;
    }
    return data;
  }

  static async getInventoryByOrganizationIdAndProductId(
    organization_id: string,
    product_id: string
  ): Promise<Stock[]> {
    const { data, error } = await supabase
      .from("inventory")
      .select("*")
      .eq("organization_id", organization_id)
      .eq("product_id", product_id);
    if (error) {
      throw error;
    }
    return data;
  }

  static async getStockByTag(
    tag: string,
    organization_id: string
  ): Promise<Stock[]> {
    const { data, error } = await supabase
      .from("inventory")
      .select("*")
      .eq("organization_id", organization_id)
      .contains("tags", [tag]);
    if (error) {
      throw error;
    }
    return data;
  }

  static async getStockByMetadata(
    key: string,
    value: string | number | boolean,
    organization_id: string
  ): Promise<Stock[]> {
    const { data, error } = await supabase
      .from("inventory")
      .select("*")
      .eq("organization_id", organization_id)
      .contains("metadata", { [key]: value });
    if (error) {
      throw error;
    }
    return data;
  }
}
